'use client';

import type { Team } from '@baseball-near-you/prisma';
import { Input, Skeleton, cn } from '@baseball-near-you/ui';
import { LeagueMapTeam } from '@bb/components/LeagueMap/LeagueMapTeam';
import { useTeams } from '@bb/lib/hooks/useTeams';
import { Search, SearchX } from 'lucide-react';

export function TeamSelectionSearch({
  search,
  setSearch,
  onTeamSelect,
  selectedTeam,
  setSelectedTeam,
  specificTeam,
  initialSearchMessage = 'Search for a team by name, city, or state',
  noResultsNode,
  highlightedTeams,
  ...props
}: {
  search: string | null;
  setSearch: (search: string) => void;
  selectedTeamSlug?: string | null;
  onTeamSelect: (team: Team) => void;
  selectedTeam?: Team | null;
  setSelectedTeam: (team: Team | null) => void;
  specificTeam?: Team | null;
  initialSearchMessage?: string;
  noResultsNode?: React.ReactNode;
  highlightedTeams?: string[];
}) {
  const { teams, teamsLoading } = useTeams({ search });

  return (
    <div>
      {!props.selectedTeamSlug && (
        <Input
          className="mb-6"
          value={search ?? ''}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search for your team..."
        />
      )}
      {teamsLoading && (
        <div className="flex max-h-min flex-col space-y-3">
          {new Array(4).fill(0).map((val) => (
            <Skeleton className="h-[5.1rem]" key={val} />
          ))}
        </div>
      )}
      {!teamsLoading && (
        <div
          className={cn(
            'flex flex-col space-y-3',
            !props.selectedTeamSlug && 'h-80 overflow-scroll sm:h-96'
          )}
        >
          {teams && teams?.length > 0 ? (
            teams?.map((team) => {
              return (
                <LeagueMapTeam
                  key={`team-search-${team.id}`}
                  team={team}
                  setCurrentTeam={(t) => setSelectedTeam(t)}
                  isCurrentTeam={selectedTeam?.slug === team.slug}
                  hideActions
                  isHighlighted={highlightedTeams?.includes(team.slug)}
                />
              );
            })
          ) : search && !teamsLoading ? (
            <div className="flex h-full w-full flex-col items-center justify-center">
              <div className="flex w-full flex-col items-center justify-center rounded-lg border-4 border-gray-300 py-16 text-gray-400">
                <SearchX size={45} className="text-gray-300" />
                <p className="mt-3 text-lg">No results found.</p>
              </div>
              {!!noResultsNode && noResultsNode}
            </div>
          ) : props.selectedTeamSlug && !!specificTeam ? (
            <div className="my-8 w-full">
              <LeagueMapTeam
                team={specificTeam as any}
                setCurrentTeam={() => setSelectedTeam(specificTeam)}
                isCurrentTeam={
                  selectedTeam?.slug === specificTeam?.slug ||
                  props.selectedTeamSlug === specificTeam?.slug
                }
                hideActions
              />
            </div>
          ) : (
            <div className="h-full w-full flex-col items-center justify-center">
              <div className="flex w-full flex-col items-center justify-center rounded-lg border-4 border-gray-300 py-16 text-gray-400">
                <Search size={45} className="text-gray-300" />
                <p className="mt-3 text-lg">{initialSearchMessage}</p>
              </div>
              {!!noResultsNode && noResultsNode}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
